import isEqual from 'lodash/isEqual';
import { isEmptyValue } from '@/components/record/fields/fieldKind';

/**
 * The combined record of several selected rows: one row holding only the
 * values every row shares, and the keys whose values differ.
 * Spec: docs/specs/sau-600-combined-record.md.
 */
export interface CombinedRows {
    /** Every key all the rows share, with the first row's value. */
    row: Record<string, unknown>;
    /** Every key on which any two rows differ. None of them is in `row`. */
    differingKeys: Set<string>;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasRecordId(value: unknown): value is Record<string, unknown> & { id: unknown } {
    return isPlainObject(value) && value.id !== null && value.id !== undefined;
}

/**
 * True when two rows hold the same value of one field. Two empty values are
 * the same, by the test one record uses to draw a dash. A related record or a
 * person compares by its id, so a display string polled a moment later does
 * not read as a different value. Two lists are the same when their items are,
 * in order. Anything else compares by deep equality.
 */
export function sameFieldValue(firstValue: unknown, secondValue: unknown): boolean {
    const firstIsEmpty = isEmptyValue(firstValue);
    const secondIsEmpty = isEmptyValue(secondValue);
    if (firstIsEmpty || secondIsEmpty) {
        return firstIsEmpty && secondIsEmpty;
    }
    if (hasRecordId(firstValue) && hasRecordId(secondValue)) {
        return String(firstValue.id) === String(secondValue.id);
    }
    if (Array.isArray(firstValue) && Array.isArray(secondValue)) {
        return (
            firstValue.length === secondValue.length &&
            firstValue.every((item, index) => sameFieldValue(item, secondValue[index]))
        );
    }
    return isEqual(firstValue, secondValue);
}

/**
 * Combines the selected rows into one. It walks every key of every row, not
 * only the columns, so a renderer that reads a sibling field, such as a link
 * reading its relation's type, finds that sibling when every row shares it.
 *
 * A key every row leaves empty is left out, so its field draws the dash. A
 * key on which any two rows differ is left out too, so no renderer can draw
 * one record's value of it. The id always differs, so it never reaches the row.
 */
export function combineRows(rows: Record<string, unknown>[]): CombinedRows {
    const row: Record<string, unknown> = {};
    const differingKeys = new Set<string>();
    const allKeys = new Set(rows.flatMap((eachRow) => Object.keys(eachRow)));
    const [firstRow, ...otherRows] = rows;

    for (const key of allKeys) {
        const firstValue = firstRow?.[key];
        if (!otherRows.every((otherRow) => sameFieldValue(firstValue, otherRow[key]))) {
            differingKeys.add(key);
        } else if (!isEmptyValue(firstValue)) {
            row[key] = firstValue;
        }
    }

    return { row, differingKeys };
}
